import React, { useState, useEffect } from 'react';
import './ConfiguracionData.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faGear,
    faPalette,
    faPhone,
    faSave,
    faRotateRight,
    faShoppingCart,
    faCheck,
    faPaperPlane,
    faVolumeHigh,
    faPlay,
    faStore
} from '@fortawesome/free-solid-svg-icons';
import Swal from 'sweetalert2';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
    getConfig,
    fetchConfigFromDB,
    saveConfigToDB,
    resetConfig,
    applyTheme,
    fetchDefaultContactPhone,
    THEME_PRESETS,
    DEFAULT_THEME_COLOR
} from '../../../utils/configService';
import { playSound, AVAILABLE_SOUNDS } from '../../../utils/soundService';

export default function ConfiguracionData() {
    const [nombreNegocio, setNombreNegocio] = useState('Restaurante & Tienda');
    const [whatsappContacto, setWhatsappContacto] = useState('');
    const [whatsappPedidos, setWhatsappPedidos] = useState('');
    const [themeColor, setThemeColor] = useState(DEFAULT_THEME_COLOR);
    const [previewColor, setPreviewColor] = useState(DEFAULT_THEME_COLOR);
    const [sonidoPedidoQR, setSonidoPedidoQR] = useState('ring_1.mp3');
    const [sonidoPedidoMesero, setSonidoPedidoMesero] = useState('ring_2.mp3');
    const [sonidoLlamadaMesero, setSonidoLlamadaMesero] = useState('beep.mp3');
    const [backendPhone, setBackendPhone] = useState('');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        cargarConfiguracion();
    }, []);

    const cargarConfiguracion = async () => {
        const local = getConfig();
        if (local) {
            setNombreNegocio(local.nombreNegocio || 'Restaurante & Tienda');
            setWhatsappContacto(local.whatsappContacto || '');
            setWhatsappPedidos(local.whatsappPedidos || '');
            setThemeColor(local.themeColor || DEFAULT_THEME_COLOR);
            setPreviewColor(local.themeColor || DEFAULT_THEME_COLOR);
            setSonidoPedidoQR(local.sonidoPedidoQR || 'ring_1.mp3');
            setSonidoPedidoMesero(local.sonidoPedidoMesero || 'ring_2.mp3');
            setSonidoLlamadaMesero(local.sonidoLlamadaMesero || 'beep.mp3');
        }

        try {
            const dbConfig = await fetchConfigFromDB();
            if (dbConfig) {
                setNombreNegocio(dbConfig.nombreNegocio || 'Restaurante & Tienda');
                setWhatsappContacto(dbConfig.whatsappContacto || '');
                setWhatsappPedidos(dbConfig.whatsappPedidos || '');
                const color = dbConfig.themeColor || DEFAULT_THEME_COLOR;
                setThemeColor(color);
                setPreviewColor(color);
                setSonidoPedidoQR(dbConfig.sonidoPedidoQR || 'ring_1.mp3');
                setSonidoPedidoMesero(dbConfig.sonidoPedidoMesero || 'ring_2.mp3');
                setSonidoLlamadaMesero(dbConfig.sonidoLlamadaMesero || 'beep.mp3');
            }
        } catch (err) {
            console.error('Error al sincronizar configuración con base de datos:', err);
        }

        const phone = await fetchDefaultContactPhone();
        setBackendPhone(phone);
        setWhatsappContacto((prev) => (!prev && phone ? phone : prev));
        setWhatsappPedidos((prev) => (!prev && phone ? phone : prev));

        setLoading(false);
    };

    const handleColorChange = (newColor) => {
        setThemeColor(newColor);
        setPreviewColor(newColor);
        applyTheme(newColor);
        window.dispatchEvent(new CustomEvent('configUpdated', { detail: { themeColor: newColor } }));
    };

    const handleSave = async () => {
        setSaving(true);

        const newConfig = {
            nombreNegocio: nombreNegocio.trim() || 'Restaurante & Tienda',
            whatsappContacto: whatsappContacto.trim(),
            whatsappPedidos: whatsappPedidos.trim(),
            themeColor: themeColor,
            sonidoPedidoQR: sonidoPedidoQR,
            sonidoPedidoMesero: sonidoPedidoMesero,
            sonidoLlamadaMesero: sonidoLlamadaMesero,
        };

        const res = await saveConfigToDB(newConfig);
        setSaving(false);

        if (res && res.error) {
            Swal.fire({
                title: 'Atención',
                text: res.error,
                icon: 'warning',
                confirmButtonColor: themeColor || '#F80050',
            });
        } else {
            Swal.fire({
                title: '¡Configuración Guardada en la Base de Datos!',
                text: 'Nombre del negocio, WhatsApp, tema y alertas sonoras han sido actualizados con éxito.',
                icon: 'success',
                confirmButtonColor: themeColor || '#F80050',
                confirmButtonText: 'Aceptar',
            });
        }
    };

    const handleReset = () => {
        Swal.fire({
            title: '¿Restablecer valores por defecto?',
            text: 'Se restaurará el nombre, color original, WhatsApp y los sonidos por defecto.',
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Sí, restablecer',
            cancelButtonText: 'Cancelar',
        }).then(async (result) => {
            if (result.isConfirmed) {
                const def = await resetConfig();
                setNombreNegocio(def.nombreNegocio);
                setThemeColor(def.themeColor);
                setPreviewColor(def.themeColor);
                setSonidoPedidoQR(def.sonidoPedidoQR);
                setSonidoPedidoMesero(def.sonidoPedidoMesero);
                setSonidoLlamadaMesero(def.sonidoLlamadaMesero);
                setWhatsappContacto(backendPhone || '');
                setWhatsappPedidos(backendPhone || '');

                Swal.fire(
                    '¡Restablecido!',
                    'Se han restablecido los valores por defecto en la base de datos.',
                    'success'
                );
            }
        });
    };

    const handleTestWhatsApp = (phone, tipo) => {
        const cleanPhone = (phone || '').replace(/\D/g, '');
        if (!cleanPhone) {
            toast.warn(`Ingresa un número válido para probar WhatsApp de ${tipo}.`, { autoClose: 2000 });
            return;
        }
        const text = tipo === 'contacto'
            ? '¡Hola! Este es un mensaje de prueba de contacto desde la configuración del restaurante.'
            : '¡Hola! Este es un mensaje de prueba de recepción de pedidos desde el restaurante.';
        const url = `https://api.whatsapp.com/send?phone=${cleanPhone}&text=${encodeURIComponent(text)}`;
        window.open(url, '_blank');
    };

    const handlePlayTestSound = (soundKey) => {
        playSound(soundKey);
        toast.info(`Reproduciendo sonido: ${soundKey}`, { autoClose: 1200 });
    };

    if (loading) {
        return (
            <div className="configuracionWrapper">
                <p>Cargando configuraciones desde la base de datos...</p>
            </div>
        );
    }

    return (
        <div className="configuracionWrapper">
            <ToastContainer />

            {/* Cabecera */}
            <div className="configuracionHeader">
                <div>
                    <h2>
                        <FontAwesomeIcon icon={faGear} /> Configuraciones del Sistema
                    </h2>
                    <p>
                        Panel exclusivo de administración. Los cambios se guardan permanentemente en la base de datos y aplican a cocina, meseros, caja y tienda.
                    </p>
                </div>
                <div className="configuracionActionsTop">
                    <button className="btnResetConfig" onClick={handleReset} disabled={saving} title="Restablecer valores iniciales">
                        <FontAwesomeIcon icon={faRotateRight} /> Restablecer
                    </button>
                    <button className="btnSaveConfig" onClick={handleSave} disabled={saving}>
                        <FontAwesomeIcon icon={faSave} /> {saving ? 'Guardando en BD...' : 'Guardar Cambios'}
                    </button>
                </div>
            </div>

            <div className="configuracionGrid">
                {/* 1. Nombre del Negocio */}
                <div className="configCard">
                    <div className="configCardHeader">
                        <FontAwesomeIcon icon={faStore} className="configCardIcon" />
                        <div>
                            <h3>Nombre del Negocio</h3>
                            <p>Se mostrará en la parte superior del menú lateral (sidebar), en la barra del sistema y en los comprobantes.</p>
                        </div>
                    </div>
                    <div className="configCardBody">
                        <div className="configFieldGroup">
                            <label>Nombre del Establecimiento / Restaurante:</label>
                            <input
                                type="text"
                                className="configInputText"
                                placeholder="Ej: Mi Restaurante Gourmet"
                                value={nombreNegocio}
                                onChange={(e) => setNombreNegocio(e.target.value)}
                            />
                        </div>
                    </div>
                </div>

                {/* 2. Personalización del Tema Visual */}
                <div className="configCard">
                    <div className="configCardHeader">
                        <FontAwesomeIcon icon={faPalette} className="configCardIcon" />
                        <div>
                            <h3>Personalización del Tema Visual</h3>
                            <p>Elige el color representativo de tu negocio. El cambio se aplica en tiempo real para todos los clientes y usuarios.</p>
                        </div>
                    </div>

                    <div className="configCardBody">
                        <label className="configLabel">Colores predeterminados sugeridos:</label>
                        <div className="themePresetsGrid">
                            {THEME_PRESETS.map((preset) => (
                                <button
                                    key={preset.color}
                                    type="button"
                                    className={`presetColorBtn ${previewColor.toLowerCase() === preset.color.toLowerCase() ? 'activePreset' : ''}`}
                                    style={{ backgroundColor: preset.color }}
                                    onClick={() => handleColorChange(preset.color)}
                                    title={preset.name}
                                >
                                    {previewColor.toLowerCase() === preset.color.toLowerCase() && (
                                        <FontAwesomeIcon icon={faCheck} className="checkIcon" />
                                    )}
                                </button>
                            ))}
                        </div>

                        <div className="customColorRow">
                            <div>
                                <label className="configLabel">O selecciona un color personalizado:</label>
                                <div className="customColorInputWrap">
                                    <input
                                        type="color"
                                        id="customColorPicker"
                                        value={previewColor}
                                        onChange={(e) => handleColorChange(e.target.value)}
                                        className="colorPickerInput"
                                    />
                                    <input
                                        type="text"
                                        value={previewColor}
                                        onChange={(e) => handleColorChange(e.target.value)}
                                        className="hexTextInput"
                                        placeholder="#F80050"
                                        maxLength={7}
                                    />
                                </div>
                            </div>

                            <div className="themeLivePreviewBox">
                                <span className="previewBadge">Vista previa</span>
                                <button
                                    type="button"
                                    className="previewExampleBtn"
                                    style={{ backgroundColor: previewColor }}
                                >
                                    Botón de ejemplo
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* 3. Configuración de WhatsApp */}
                <div className="configCard">
                    <div className="configCardHeader">
                        <FontAwesomeIcon icon={faPhone} className="configCardIcon" />
                        <div>
                            <h3>Configuración de WhatsApp</h3>
                            <p>Configura de forma independiente el número para consultas y el número receptor de pedidos.</p>
                        </div>
                    </div>

                    <div className="configCardBody">
                        <div className="configFieldGroup">
                            <label>
                                <strong>WhatsApp de Contacto Directo:</strong>
                                <span>Utilizado por el botón flotante en la tienda para consultas generales.</span>
                            </label>
                            <div className="inputWithAction">
                                <input
                                    type="text"
                                    className="configInputText"
                                    placeholder="Ej: +591 78901234 ó 59178901234"
                                    value={whatsappContacto}
                                    onChange={(e) => setWhatsappContacto(e.target.value)}
                                />
                                <button
                                    type="button"
                                    className="btnTestWhatsapp"
                                    onClick={() => handleTestWhatsApp(whatsappContacto, 'contacto')}
                                    title="Probar enlace de WhatsApp Contacto"
                                >
                                    <FontAwesomeIcon icon={faPaperPlane} /> Probar
                                </button>
                            </div>
                        </div>

                        <div className="configFieldGroup">
                            <label>
                                <strong>WhatsApp Receptor de Pedidos:</strong>
                                <span>Número al que los clientes envían el detalle de sus pedidos desde el carrito de compras.</span>
                            </label>
                            <div className="inputWithAction">
                                <input
                                    type="text"
                                    className="configInputText"
                                    placeholder="Ej: +591 78901234 ó 59178901234"
                                    value={whatsappPedidos}
                                    onChange={(e) => setWhatsappPedidos(e.target.value)}
                                />
                                <button
                                    type="button"
                                    className="btnTestWhatsapp"
                                    onClick={() => handleTestWhatsApp(whatsappPedidos, 'pedidos')}
                                    title="Probar enlace de WhatsApp Pedidos"
                                >
                                    <FontAwesomeIcon icon={faShoppingCart} /> Probar
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* 4. Configuración de Alertas Sonoras */}
                <div className="configCard">
                    <div className="configCardHeader">
                        <FontAwesomeIcon icon={faVolumeHigh} className="configCardIcon" />
                        <div>
                            <h3>Configuración de Alertas Sonoras</h3>
                            <p>Elige el sonido MP3 que sonará en cocina y meseros ante nuevos pedidos y llamadas de mesas.</p>
                        </div>
                    </div>

                    <div className="configCardBody">
                        {/* Evento 1: Pedido por QR */}
                        <div className="configFieldGroup">
                            <label>
                                <strong>Alerta para Pedidos QR en Mesa (Cocina / KDS):</strong>
                                <span>Sonará en el panel de cocina cuando un cliente ordene desde su mesa mediante código QR.</span>
                            </label>
                            <div className="soundSelectorRow">
                                <select
                                    className="configSelect"
                                    value={sonidoPedidoQR}
                                    onChange={(e) => setSonidoPedidoQR(e.target.value)}
                                >
                                    {AVAILABLE_SOUNDS.map((s) => (
                                        <option key={s.id} value={s.id}>{s.name} ({s.file})</option>
                                    ))}
                                </select>
                                <button
                                    type="button"
                                    className="btnTestSound"
                                    onClick={() => handlePlayTestSound(sonidoPedidoQR)}
                                    title="Escuchar sonido"
                                >
                                    <FontAwesomeIcon icon={faPlay} /> Escuchar
                                </button>
                            </div>
                        </div>

                        {/* Evento 2: Pedido por Mesero */}
                        <div className="configFieldGroup">
                            <label>
                                <strong>Alerta para Pedidos de Meseros (Cocina / KDS):</strong>
                                <span>Sonará en la cocina cuando un mesero ingrese una nueva comanda desde su panel.</span>
                            </label>
                            <div className="soundSelectorRow">
                                <select
                                    className="configSelect"
                                    value={sonidoPedidoMesero}
                                    onChange={(e) => setSonidoPedidoMesero(e.target.value)}
                                >
                                    {AVAILABLE_SOUNDS.map((s) => (
                                        <option key={s.id} value={s.id}>{s.name} ({s.file})</option>
                                    ))}
                                </select>
                                <button
                                    type="button"
                                    className="btnTestSound"
                                    onClick={() => handlePlayTestSound(sonidoPedidoMesero)}
                                    title="Escuchar sonido"
                                >
                                    <FontAwesomeIcon icon={faPlay} /> Escuchar
                                </button>
                            </div>
                        </div>

                        {/* Evento 3: Llamada al Mesero */}
                        <div className="configFieldGroup">
                            <label>
                                <strong>Alerta para Llamada de Clientes (Panel de Meseros):</strong>
                                <span>Sonará en el panel de meseros cuando un cliente presione "Llamar al mesero" en su mesa.</span>
                            </label>
                            <div className="soundSelectorRow">
                                <select
                                    className="configSelect"
                                    value={sonidoLlamadaMesero}
                                    onChange={(e) => setSonidoLlamadaMesero(e.target.value)}
                                >
                                    {AVAILABLE_SOUNDS.map((s) => (
                                        <option key={s.id} value={s.id}>{s.name} ({s.file})</option>
                                    ))}
                                </select>
                                <button
                                    type="button"
                                    className="btnTestSound"
                                    onClick={() => handlePlayTestSound(sonidoLlamadaMesero)}
                                    title="Escuchar sonido"
                                >
                                    <FontAwesomeIcon icon={faPlay} /> Escuchar
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Botón flotante inferior para guardar */}
            <div className="configuracionFooterSave">
                <button className="btnSaveConfigLarge" onClick={handleSave} disabled={saving}>
                    <FontAwesomeIcon icon={faSave} /> {saving ? 'Guardando cambios en Base de Datos...' : 'Guardar Todas las Configuraciones'}
                </button>
            </div>
        </div>
    );
}
